"use client"

import { Plus, FileText } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface FileUploadCardProps {
  onStartUpload: () => void
}

// 圆形进度指示器组件
function CircularProgress({ percentage, label }: { percentage: number; label: string }) {
  const radius = 16
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (percentage / 100) * circumference

  return (
    <div className="flex items-center gap-2">
      <div className="relative w-10 h-10">
        <svg className="w-10 h-10 -rotate-90" viewBox="0 0 40 40">
          {/* 背景圆环 */}
          <circle
            cx="20"
            cy="20"
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="3"
          />
          {/* 进度圆环 */}
          <circle
            cx="20"
            cy="20"
            r={radius}
            fill="none"
            stroke="#fbbf24"
            strokeWidth="3"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div className="flex items-center gap-1 text-xs text-gray-600">
        <span>{label}</span>
        <span className="font-medium text-gray-900">{percentage}%</span>
      </div>
    </div>
  )
}

export function FileUploadCard({ onStartUpload }: FileUploadCardProps) {
  return (
    <Card className="bg-white rounded-2xl shadow-lg p-5 space-y-4 w-full max-w-sm">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-medium text-gray-900">文件传输</h3>
        <Button variant="ghost" size="icon" className="text-yellow-500 hover:bg-yellow-50 h-8 w-8">
          <Plus className="w-5 h-5" />
        </Button>
      </div>

      <div className="space-y-3">
        {/* 文件信息 */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
          <FileText className="w-8 h-8 text-gray-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              PromptD...nHr-64-Appimage
            </p>
          </div>
        </div>

        {/* 文件大小 */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            约 <span className="font-medium">1 M</span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-500">19.30 MB</span>
          </p>
        </div>

        {/* 进度指示器 */}
        <div className="flex items-center justify-between px-2">
          <CircularProgress percentage={100} label="2d*7*2d" />
          <div className="w-px h-8 bg-gray-200" />
          <CircularProgress percentage={100} label="7d*7*9d" />
        </div>

        {/* 有效期 */}
        <div className="space-y-1.5">
          <label className="text-sm text-gray-700 font-medium">有效期</label>
          <Select defaultValue="1">
            <SelectTrigger className="w-full bg-white border-gray-200 rounded-lg h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 天</SelectItem>
              <SelectItem value="3">3 天</SelectItem>
              <SelectItem value="7">7 天</SelectItem>
              <SelectItem value="30">30 天</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* 分享密码 */}
        <div className="space-y-1.5">
          <label className="text-sm text-gray-700 font-medium">分享密码</label>
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-500">无</span>
          </div>
        </div>

        {/* 用户协议 */}
        <div className="flex items-start gap-2 pt-1">
          <Checkbox id="terms" className="mt-0.5" />
          <label htmlFor="terms" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
            已同意使用作品公约以及《使用协议》和《隐私政策》等平台相关规则指引
          </label>
        </div>

        {/* 开始上传按钮 */}
        <Button
          className="w-full h-11 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-full font-medium text-sm shadow-sm"
          onClick={onStartUpload}
        >
          开始上传
        </Button>
      </div>
    </Card>
  )
}
